import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RiNotification3Line,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiInformationLine,
  RiAlarmWarningLine,
} from '@remixicon/react';
import { useNotifications, type NotificationDto } from '@/lib/api/queries';
import { formatRelativeTime, formatDateTime } from '@/lib/format';

/**
 * BİLDİRİM ZİLİ.
 *
 * KAYNAK `outbox_event` TABLOSUDUR — AYRI BİR "BİLDİRİM" TABLOSU DEĞİL.
 *
 * Kullanıcıya gösterilen bildirim ile kiracıya giden e-posta/SMS aynı
 * kayıttan doğar. İki ayrı yol olsaydı eninde sonunda ayrışırlardı:
 * kiracıya e-posta gider ama panelde görünmez, ya da tersi. Tek kaynak,
 * "ben böyle bir bildirim almadım" tartışmasını da bitirir.
 *
 * ROZET SAYISI ABARTILMAZ: yalnızca henüz gönderilmemiş (PENDING) olaylar
 * sayılır. Kişi bazında "okundu" takibi bildirim merkezinin kendi işidir;
 * o gelene kadar yanlış bir sayı göstermektense az ama doğru bir sayı
 * gösteriyoruz.
 */

const TONE_ICON = {
  success: RiCheckboxCircleLine,
  danger: RiErrorWarningLine,
  warning: RiAlarmWarningLine,
  info: RiInformationLine,
} as const;

/** Olay tipinden ilgili ekrana bağlantı üretir. */
function targetFor(n: NotificationDto): string {
  if (n.eventType.startsWith('turnover.declaration')) return '/ciro/onay-kuyrugu';
  if (n.eventType.startsWith('turnover')) return '/ciro/beyanlar';
  if (n.eventType.startsWith('invoice')) return '/finans/faturalar';
  if (n.eventType.startsWith('payment')) return '/finans/tahsilatlar';
  if (n.eventType.startsWith('ticket')) return '/operasyon/talepler';
  if (n.eventType.startsWith('lease')) return '/sozlesmeler/bitisler';
  if (n.eventType.startsWith('document')) return '/guvenlik/belgeler';
  return '/dashboard';
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const query = useNotifications();

  /*
    DIŞARI TIKLAYINCA KAPANIR.

    react-bootstrap Dropdown yalnızca `Dropdown.Item` tıklanınca kapanır;
    bizim içeriğimiz zengin (ikon + iki satır + zaman) ve `Item` değil.
    Bu yüzden açılır paneli kendimiz yönetiyoruz.
  */
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const items = query.data?.items ?? [];
  const unread = query.data?.unread ?? 0;

  return (
    <div className="notif" ref={containerRef}>
      <button
        type="button"
        className="header-icon-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Bildirimler (${unread} yeni)` : 'Bildirimler'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <RiNotification3Line size={19} aria-hidden="true" />
        {unread > 0 && (
          <span className="header-icon-btn__badge bg-danger">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif__panel" role="dialog" aria-label="Bildirimler">
          <div className="notif__header">
            <span className="fw-semibold fs-13">Bildirimler</span>
            <span className="fs-11 text-muted">
              {unread > 0 ? `${unread} yeni` : 'Yeni bildirim yok'}
            </span>
          </div>

          <div className="notif__list">
            {query.isLoading && <div className="notif__empty">Yükleniyor…</div>}

            {!query.isLoading && items.length === 0 && (
              <div className="notif__empty">
                Bildirim yok.
                <div className="fs-11 mt-1">
                  Ciro beyanı, fatura ve SLA olayları burada görünür.
                </div>
              </div>
            )}

            {items.map((n) => {
              const Icon = TONE_ICON[n.tone as keyof typeof TONE_ICON] ?? RiInformationLine;

              return (
                <Link
                  key={n.id}
                  to={targetFor(n)}
                  className={`notif__item${n.status === 'PENDING' ? ' is-unread' : ''}`}
                  onClick={() => setOpen(false)}
                >
                  <span className={`notif__icon text-${n.tone}`}>
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <span className="notif__body">
                    <span className="notif__title">{n.title}</span>
                    <span className="notif__desc">{n.description}</span>
                  </span>
                  {/* Göreli zaman hızlı okunur; tam zaman ipucunda kalır ki
                      "tam olarak ne zaman" sorusu da cevapsız kalmasın. */}
                  <span className="notif__time" title={formatDateTime(n.createdAt)}>
                    {formatRelativeTime(n.createdAt)}
                  </span>
                </Link>
              );
            })}
          </div>

          <Link to="/sistem/bildirimler" className="notif__footer" onClick={() => setOpen(false)}>
            Tüm bildirimleri gör
          </Link>
        </div>
      )}
    </div>
  );
}
